const apiUrl = import.meta.env.VITE_NBH_URL_ROOT;

export interface StatisticsDataRange {
    earliestDate: string | null;
    earliestOrderDate: string | null;
    latestDate: string | null;
    importDataStart: string | null;
    importDataEnd: string | null;
}

export async function getDataRange(token: string): Promise<StatisticsDataRange> {
    const response = await fetch(`${apiUrl}/api/statistics/data-range`, {
        headers: {Authorization: `Bearer ${token}`},
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch data range: ${response.statusText}`);
    }

    return response.json();
}

export async function downloadPointsPerMemberExport(token: string, asOfDate?: string): Promise<void> {
    const url = asOfDate
        ? `${apiUrl}/api/statistics/export/points-per-member?asOfDate=${asOfDate}`
        : `${apiUrl}/api/statistics/export/points-per-member`;

    const response = await fetch(url, {
        headers: {Authorization: `Bearer ${token}`},
    });

    if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    downloadBlob(blob, getFilenameFromResponse(response) || generateFilename('punkte-pro-mitglied', 'xlsx'));
}

export async function downloadContractorsClientsExport(
    token: string,
    startDate: string,
    endDate: string,
): Promise<void> {
    const response = await fetch(
        `${apiUrl}/api/statistics/export/contractors-clients?startDate=${startDate}&endDate=${endDate}`,
        {
            headers: {Authorization: `Bearer ${token}`},
        }
    );

    if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    downloadBlob(blob, getFilenameFromResponse(response) || generateFilename('auftragnehmer-auftraggeber', 'xlsx'));
}

export async function downloadMemberDeploymentsExport(
    token: string,
    startDate: string,
    endDate: string,
): Promise<void> {
    const response = await fetch(
        `${apiUrl}/api/statistics/export/member-deployments?startDate=${startDate}&endDate=${endDate}`,
        {
            headers: {Authorization: `Bearer ${token}`},
        }
    );

    if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    downloadBlob(blob, getFilenameFromResponse(response) || generateFilename('mitglieder-einsaetze', 'xlsx'));
}

function downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
}

function getFilenameFromResponse(response: Response): string | null {
    const disposition = response.headers.get('Content-Disposition');
    if (!disposition) return null;
    const match = disposition.match(/filename="?([^"]+)"?/);
    return match ? match[1] : null;
}

function generateFilename(name: string, extension: string): string {
    const date = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    return `${date}-${name}.${extension}`;
}

export interface ServiceGroupItem {
    id: string;
    name: string;
}

export async function getServiceGroups(token: string): Promise<ServiceGroupItem[]> {
    const response = await fetch(`${apiUrl}/api/statistics/service-groups`, {
        headers: {Authorization: `Bearer ${token}`},
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch service groups: ${response.statusText}`);
    }

    return response.json();
}

export async function downloadServiceListsExport(
    token: string,
    serviceGroupIds: string[] | null,
): Promise<void> {
    const url = serviceGroupIds && serviceGroupIds.length > 0
        ? `${apiUrl}/api/statistics/export/service-lists?serviceGroupIds=${serviceGroupIds.join(',')}`
        : `${apiUrl}/api/statistics/export/service-lists`;

    const response = await fetch(url, {
        headers: {Authorization: `Bearer ${token}`},
    });

    if (!response.ok) {
        throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    downloadBlob(blob, getFilenameFromResponse(response) || generateFilename('servicelisten', 'xlsx'));
}
